// src/utils/xlsx.ts
import ExcelJS from 'exceljs';
import { CsvColumn } from './csv';
import { AllPointOfInterestTemplate } from '@/types';

export type XlsxColumn = CsvColumn;

export type XlsxProcessorConstructor = {
  columns: XlsxColumn[];
  sheetName?: string;
};

// Define a type for cell data
type CellValue = string | number | boolean | null | undefined;

/**
 * Represents an XLSX processor that provides methods for working with Excel data.
 */
export class XlsxProcessor {
  /**
   * An array of XLSX columns, each containing a label and an array of data values.
   */
  private columns: { label: string; data: CellValue[] }[];
  
  /**
   * The name of the worksheet.
   */
  private sheetName: string;

  /**
   * Creates a new XlsxProcessor instance.
   * @param {XlsxProcessorConstructor} options - The options to initialize the XlsxProcessor with.
   */
  constructor({ columns, sheetName = 'Sheet1' }: { columns: { label: string; data: CellValue[] }[]; sheetName?: string }) {
    this.columns = columns;
    this.sheetName = sheetName;
  }

  /**
   * Generates an Excel workbook from the data.
   * @returns {Promise<ExcelJS.Workbook>} The Excel workbook.
   */
  async createWorkbook(): Promise<ExcelJS.Workbook> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.sheetName);

    // Add headers
    worksheet.columns = this.columns.map((column) => ({
      header: column.label,
      key: column.label,
      width: 20,
    }));

    // Add data rows
    const maxRows = Math.max(...this.columns.map((column) => column.data.length));
    for (let i = 0; i < maxRows; i++) {
      const row: Record<string, CellValue> = {};
      this.columns.forEach((column) => {
        row[column.label] = i < column.data.length ? column.data[i] : '';
      });
      worksheet.addRow(row);
    }

    return workbook;
  }

  /**
   * Downloads the Excel data as an XLSX file.
   * @param {string} filename - The name of the file to download.
   */
  async download(filename: string): Promise<void> {
    const workbook = await this.createWorkbook();
    
    // In browser environment
    if (typeof window !== 'undefined') {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      
      window.URL.revokeObjectURL(url);
    } else {
      // In Node.js environment (for testing)
      await workbook.xlsx.writeFile(filename);
    }
  }

  /**
   * Loads new Excel data, replacing the existing columns.
   * @param {XlsxProcessorConstructor} options - The options containing new columns to load.
   */
  load({ columns, sheetName }: XlsxProcessorConstructor): void {
    this.columns = columns;
    if (sheetName) this.sheetName = sheetName;
  }

  /**
   * Retrieves a specific Excel column by its label.
   * @param {string} label - The label of the column to retrieve.
   * @returns {XlsxColumn | undefined} The Excel column with the specified label, or undefined if not found.
   */
  getColumn(label: string): { label: string; data: CellValue[] } | undefined {
    return this.columns.find((column) => column.label === label);
  }

  /**
   * Adds a new column to the Excel data.
   * @param {string} label - The label for the new column.
   * @param {Array<string|number|boolean|null|undefined>} data - An array of data values for the new column.
   */
  addColumn(label: string, data: Array<string|number|boolean|null|undefined>): void {
    this.columns.push({ label, data });
  }

  /**
   * Removes an Excel column by its label.
   * @param {string} label - The label of the column to remove.
   */
  removeColumn(label: string): void {
    this.columns = this.columns.filter((column) => column.label !== label);
  }
}

export const downloadArrayAsXlsx = async (headers: string[], filename: string): Promise<void> => {
  const xlsx = new XlsxProcessor({
    columns: headers.map((header) => ({ label: header, data: [] })),
  });

  await xlsx.download(filename);
};

export class XlsxTemplateProcessor {
  private template: AllPointOfInterestTemplate[0];
  
  constructor(template: AllPointOfInterestTemplate[0]) {
    this.template = template;
  }
  
  async download(filename: string): Promise<void> {
    // Get template fields
    const fields = this.template.fields.filter(field => !field.isHidden);
    
    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.template.name);
    
    // Add headers
    worksheet.columns = fields.map(field => ({
      header: field.name,
      key: field.name,
      width: 20
    }));
    
    // Add an empty example row
    worksheet.addRow(fields.reduce((row, field) => {
      row[field.name] = '';
      return row;
    }, {} as Record<string, string>));
    
    // In browser environment
    if (typeof window !== 'undefined') {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      
      window.URL.revokeObjectURL(url);
    } else {
      // In Node.js environment (for testing)
      await workbook.xlsx.writeFile(filename);
    }
  }
}